import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppLayout from "@/components/feature/AppLayout";
import DramaCard from "@/components/feature/DramaCard";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import FilterBar from "@/pages/category/components/FilterBar";
import { dramas } from "@/mocks/dramas";

const PAGE_SIZE = 12;

function parseViews(views: string): number {
  if (views.includes("亿")) return parseFloat(views) * 1e8;
  if (views.includes("万")) return parseFloat(views) * 1e4;
  const value = parseFloat(views);
  return Number.isNaN(value) ? 0 : value;
}

export default function Category() {
  const [searchParams] = useSearchParams();
  const keyword = (searchParams.get("q") ?? "").trim();

  const [status, setStatus] = useState("全部");
  const [year, setYear] = useState("全部");
  const [lead, setLead] = useState("全部");
  const [sort, setSort] = useState("updated");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useDocumentMeta({
    title: "出轨的味道影视剧全集分类 - 欧美剧/国产剧等",
    description: "在出轨的味道影视剧分类页浏览她的热门影视作品，查看详细剧情简介及演员信息。",
    keywords: "出轨的味道,影视分类,电视剧全集,剧情介绍,演员名单,都市情感剧,悬疑剧",
  });

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const years = useMemo(
    () => Array.from(new Set(dramas.map((item) => String(item.year)))).sort((a, b) => Number(b) - Number(a)),
    []
  );

  const leads = useMemo(() => {
    const names = dramas.map((item) => item.cast[0]?.name).filter(Boolean) as string[];
    return Array.from(new Set(names)).slice(0, 8);
  }, []);

  const filtered = useMemo(() => {
    const list = dramas.filter((item) => {
      if (status !== "全部" && item.status !== status) return false;
      if (year !== "全部" && String(item.year) !== year) return false;
      if (lead !== "全部" && !item.cast.some((member) => member.name === lead)) return false;
      if (keyword) {
        const haystack = [item.title, item.synopsis, item.genres.join(" "), item.cast.map((m) => m.name).join(" ")].join(" ");
        if (!haystack.includes(keyword)) return false;
      }
      return true;
    });

    return list.sort((a, b) => {
      if (sort === "hot") return parseViews(b.views) - parseViews(a.views);
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });
  }, [status, year, lead, sort, keyword]);

  useEffect(() => {
    setPage(1);
  }, [status, year, lead, sort, keyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  const visible = isMobile
    ? filtered.slice(0, page * PAGE_SIZE)
    : filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleReset = () => {
    setStatus("全部");
    setYear("全部");
    setLead("全部");
    setSort("updated");
  };

  return (
    <AppLayout>
      <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-8 md:py-10">
        <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-foreground-500">
          <Link to="/" className="transition-colors hover:text-accent-700 cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">影视分类</span>
        </nav>

        <header className="mt-5 mb-6">
          <p className="font-label text-[11px] tracking-[0.3em] text-accent-600">CATEGORY</p>
          <h1 className="mt-2 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            出轨的味道影视剧全集分类
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
            汇集“出轨的味道”系列及相关都市情感、家庭伦理、悬疑推理题材影视剧合集，可按状态、年份与主演筛选，支持按更新时间与热门程度排序。
          </p>
          {keyword && (
            <p className="mt-3 flex flex-wrap items-center gap-2 text-sm text-foreground-700">
              <span className="flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-accent-800">
                <i className="ri-search-line"></i>
                搜索关键词：{keyword}
              </span>
              <Link to="/category" className="text-xs text-primary-600 transition-colors hover:text-accent-700 cursor-pointer">
                清除搜索
              </Link>
            </p>
          )}
        </header>

        <FilterBar
          status={status}
          onStatus={setStatus}
          year={year}
          onYear={setYear}
          lead={lead}
          onLead={setLead}
          sort={sort}
          onSort={setSort}
          years={years}
          leads={leads}
          open={filterOpen}
          onToggle={() => setFilterOpen((prev) => !prev)}
          onReset={handleReset}
          resultCount={filtered.length}
        />

        {filtered.length === 0 ? (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-20 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-film-line text-3xl"></i>
            </span>
            <p className="mt-5 text-base font-medium text-foreground-900">暂无符合条件的影视剧</p>
            <p className="mt-2 text-sm text-foreground-500">换个筛选条件试试，或者重置后再看看全部内容。</p>
            <button
              type="button"
              onClick={handleReset}
              className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {visible.map((drama) => (
                <DramaCard key={drama.id} drama={drama} />
              ))}
            </div>

            {/* Desktop pagination */}
            <div className="mt-10 hidden items-center justify-center gap-2 md:flex">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors enabled:hover:border-accent-400 enabled:hover:text-accent-700 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
              >
                <i className="ri-arrow-left-s-line text-xl"></i>
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => setPage(num)}
                  className={`flex h-9 min-w-9 items-center justify-center rounded-md px-3 text-sm transition-colors cursor-pointer ${
                    num === currentPage
                      ? "bg-primary-600 text-background-50"
                      : "border border-background-300 text-foreground-700 hover:border-accent-400 hover:text-accent-700"
                  }`}
                >
                  {num}
                </button>
              ))}
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors enabled:hover:border-accent-400 enabled:hover:text-accent-700 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
              >
                <i className="ri-arrow-right-s-line text-xl"></i>
              </button>
            </div>

            {/* Mobile load more */}
            <div className="mt-8 flex justify-center md:hidden">
              {page * PAGE_SIZE < filtered.length ? (
                <button
                  type="button"
                  onClick={() => setPage((prev) => prev + 1)}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-accent-400 hover:text-accent-700 cursor-pointer"
                >
                  <i className="ri-add-line"></i>
                  加载更多
                </button>
              ) : (
                <p className="text-xs text-foreground-400">已经到底啦，共 {filtered.length} 部作品</p>
              )}
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}